/**
 * Change requests against a contracted agent, and how each carrier processes
 * them.
 *
 * Two separate concerns live here:
 *
 * 1. **Who may ask for what.** Every request type names the roles allowed to
 *    submit it. An upline may reshape their own team, but money and
 *    terminations stay with staff.
 * 2. **How the request reaches the carrier.** Each carrier is worked through
 *    exactly one channel — its own portal, an email, a partner (PPG/SMS), or
 *    Reagan.ai — and the routing table is the source of truth for that.
 *
 * Everything here is pure data plus helpers, so the portal, the API, and the
 * eventual automation all agree on the rules.
 */
import { Carrier, CARRIER_SLUG_TO_CARRIER } from "./carriers.js";
export const CHANGE_REQUEST_TYPES = [
    "COMMISSION_TYPE",
    "CONTRACT_TYPE",
    "HIERARCHY",
    "COMMISSION_LEVEL",
    "TERMINATION",
];
export const CHANGE_REQUEST_TYPE_LABELS = {
    COMMISSION_TYPE: "Advance / As Earned",
    CONTRACT_TYPE: "Agent / Agency",
    HIERARCHY: "Hierarchy change",
    COMMISSION_LEVEL: "Commission change",
    TERMINATION: "Terminate agent",
};
/**
 * Roles allowed to submit each request type.
 *
 * Commission level and termination are deliberately narrower than the rest:
 * an upline can restructure their team but cannot move money or end someone's
 * contract.
 */
export const CHANGE_REQUEST_ACCESS = {
    COMMISSION_TYPE: ["STAFF", "ADMIN", "UPLINE"],
    CONTRACT_TYPE: ["STAFF", "ADMIN", "UPLINE"],
    HIERARCHY: ["STAFF", "ADMIN", "UPLINE"],
    COMMISSION_LEVEL: ["STAFF", "ADMIN"],
    TERMINATION: ["STAFF"],
};
export function canSubmitChangeRequest(actor, type) {
    return CHANGE_REQUEST_ACCESS[type].includes(actor);
}
/** The request types one actor may submit, in menu order. */
export function changeRequestTypesFor(actor) {
    return CHANGE_REQUEST_TYPES.filter((type) => canSubmitChangeRequest(actor, type));
}
/**
 * A contract is only selectable on a change request once the carrier has
 * issued a writing number against an active contract — before that there is
 * nothing at the carrier to change.
 */
export function isChangeRequestSelectableContract(contract) {
    if (contract.status !== "ACTIVE")
        return false;
    return Boolean(contract.writingNumber?.trim() || contract.agentWritingNumber?.trim());
}
/** Carriers Reagan.ai processes, all with the same 60-day countdown behavior. */
const REAGAN_AI_CARRIERS = [
    Carrier.BALTIMORE_LIFE,
    Carrier.F_AND_G,
    Carrier.INSTABRAIN,
    Carrier.FORESTERS,
    Carrier.ILLINOIS_MUTUAL,
    Carrier.KANSAS_CITY_LIFE,
    Carrier.LIBERTY_BANKERS,
    Carrier.ROYAL_ARCANUM,
];
/** Carriers an IMO partner works on our behalf. */
const PARTNER_CARRIERS = [
    [Carrier.COREBRIDGE, "PPG"],
    [Carrier.ROYAL_NEIGHBORS, "PPG"],
    [Carrier.AMERICAN_AMICABLE, "PPG"],
    [Carrier.GERBER, "SMS"],
    [Carrier.MUTUAL_OF_OMAHA, "SMS"],
];
export const TRANSAMERICA_CONTRACT_ADMIN_EMAIL = "TLP-CRcontractadmin@transamerica.com";
/** Reagan.ai carriers quote a 60-day window; the portal counts it down. */
export const REAGAN_AI_COUNTDOWN_DAYS = 60;
export const CARRIER_CHANGE_ROUTES = {
    [Carrier.AFLAC]: {
        channel: "CARRIER_PORTAL",
        portalUrl: "http://www.sellaflacseniorplans.com/",
    },
    [Carrier.AMERICAN_HOME_LIFE]: {
        channel: "CARRIER_PORTAL",
        portalUrl: "https://www.aetnaseniorproducts.com/ssibrokerwebaz/agent/management/dashboard?channel=amh",
    },
    [Carrier.TRANSAMERICA]: {
        channel: "EMAIL",
        email: TRANSAMERICA_CONTRACT_ADMIN_EMAIL,
    },
    ...Object.fromEntries(PARTNER_CARRIERS.map(([carrier, partner]) => [
        carrier,
        { channel: "PARTNER", partner },
    ])),
    ...Object.fromEntries(REAGAN_AI_CARRIERS.map((carrier) => [
        carrier,
        { channel: "REAGAN_AI", countdownDays: REAGAN_AI_COUNTDOWN_DAYS },
    ])),
};
/**
 * How a carrier's change requests are processed, by enum value or the
 * lowercase contract slug. Null means we have no documented route yet — the
 * request still stands, it just has no automated path.
 */
export function carrierChangeRoute(carrier) {
    if (!carrier)
        return null;
    const resolved = CARRIER_SLUG_TO_CARRIER[carrier.trim().toLowerCase()];
    return resolved ? CARRIER_CHANGE_ROUTES[resolved] ?? null : null;
}
/**
 * Subject for a carrier contract-admin email:
 * "John Johnson (MLSR1234567) – Commission Change Request"
 */
export function buildCarrierChangeEmailSubject(input) {
    return `${input.agentName} (${input.agentWritingNumber}) – ${input.requestReason}`;
}
/** Body for a carrier contract-admin email. */
export function buildCarrierChangeEmailBody(input) {
    return [
        "To whom it may concern,",
        "",
        `Could you please assist us with ${input.agentName}'s request for ${input.requestReason} change.`,
        "",
        "Please let us know if there is anything else we can provide further.",
        "",
        "Thank you.",
    ].join("\n");
}
/** Days remaining on a carrier's quoted window, floored at zero. */
export function countdownDaysRemaining(submittedAt, totalDays, now) {
    const elapsedMs = now.getTime() - submittedAt.getTime();
    const elapsedDays = Math.floor(elapsedMs / (24 * 60 * 60 * 1000));
    return Math.max(0, totalDays - elapsedDays);
}
